import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import classNames from 'classnames';
import { withImagesData, withUser } from 'core/hocs';
import { FormattedTag } from 'core/components';

import { injectIntl, intlShape } from 'react-intl';
import { USER_FIELDS } from 'core/constants';
import { IconGrid } from 'components/icons/icon-grid/icon-grid';
import { IconList } from 'components/icons/icon-list/icon-list';
import {
  enrichRegistrationBonuses,
  filterOutExpiredBonuses,
  filterOutFutureBonuses,
} from 'src/helpers/utils';

import { BonusSelectionGridViewUI } from './bonus-selection-grid-view/bonus-selection-grid-view';
import { BonusSelectionListViewUI } from './bonus-selection-list-view/bonus-selection-list-view';
import { defineUnavailableBonusTypes } from '../../../../helpers/payments';

import './bonus-selection.scss';

export class BonusSelectionUI extends Component {
  static propTypes = {
    currency: PropTypes.string.isRequired,
    className: PropTypes.string,
    bonusTypes: PropTypes.arrayOf(
      PropTypes.shape({
        label: '',
        value: '',
      })
    ).isRequired,
    allBonusTypes: PropTypes.arrayOf(PropTypes.shape({})),
    depositBonuses: PropTypes.arrayOf(PropTypes.shape({})),
    bonusActive: PropTypes.string,
    changeBonusHandler: PropTypes.func,
    isInProgress: PropTypes.bool.isRequired,
    isModal: PropTypes.bool,
    updateModalAlignment: PropTypes.func,
    imagesData: PropTypes.shape().isRequired,
    userData: PropTypes.shape().isRequired,
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    className: '',
    bonusActive: '',
    depositBonuses: null,
    allBonusTypes: [],
    isModal: false,
    updateModalAlignment: null,
    changeBonusHandler: () => {},
  };

  state = {
    isShouldGoToSlide: true,
    isGridView: false,
  };

  sliderRef = createRef();

  componentDidUpdate(prevProps) {
    const { isInProgress: prevIsInProgress } = prevProps;
    const { isInProgress, updateModalAlignment } = this.props;

    if (!isInProgress && prevIsInProgress) {
      if (updateModalAlignment) {
        updateModalAlignment();
      }

      this.toggleForceGoToSlide(true);
    }
  }

  forceGoToSlide = () => {
    const { bonusTypes, bonusActive } = this.props;
    const slickIndex = R.findIndex(R.propEq('value', bonusActive))(bonusTypes);
    this.sliderRef.current.slickGoTo(slickIndex);
    this.toggleForceGoToSlide(false);
  };

  toggleForceGoToSlide = (val) => {
    this.setState({ isShouldGoToSlide: val });
  };

  onListViewIconClick = () => {
    this.setState({ isGridView: false });
  }

  onGridViewIconClick = () => {
    this.setState({ isGridView: true });
  }

  render() {
    const {
      className,
      bonusTypes,
      allBonusTypes,
      bonusActive,
      changeBonusHandler,
      currency,
      isInProgress,
      isModal,
      imagesData,
      userData: {
        regDate,
      },
      intl,
      depositBonuses,
      updateModalAlignment
    } = this.props;

    const { isShouldGoToSlide, isGridView } = this.state;

    const unavailableBonuses = defineUnavailableBonusTypes(bonusTypes, allBonusTypes);

    const targetBonusesList = [...(bonusTypes.slice(0, -1)), ...unavailableBonuses, ...(bonusTypes.slice(-1))];

    const bonuses = filterOutFutureBonuses(
      filterOutExpiredBonuses(enrichRegistrationBonuses(targetBonusesList, intl, regDate))
    );

    if (isShouldGoToSlide && this.sliderRef.current && bonusActive) {
      this.forceGoToSlide();
    }

    return (
      <div className={classNames('bonus-selection mx-n1_5 mx-sm-n3', className)}>
        <div
          className="d-flex justify-content-between align-items-center mx-sm-3 mx-1_5 pb-1_5"
        >
          <FormattedTag
            tag="div"
            id="profile.deposit-new.choose-bonus"
            className="bonus-headline d-block text-white h4"
          />

          <div className="d-flex bonus-selection-view-icons">
            <div
              role="button"
              tabIndex="0"
              onClick={this.onGridViewIconClick}
              onKeyPress={this.onGridViewIconClick}
            >
              <IconGrid className={classNames({ 'icon-selected': isGridView })} />
            </div>

            <div
              role="button"
              tabIndex="0"
              onClick={this.onListViewIconClick}
              onKeyPress={this.onListViewIconClick}
            >
              <IconList className={classNames({ 'icon-selected': !isGridView })} />
            </div>
          </div>
        </div>

        {isGridView ? (
          <BonusSelectionGridViewUI
            currency={currency}
            bonusTypes={bonusTypes}
            bonuses={bonuses}
            depositBonuses={depositBonuses}
            imagesData={imagesData}
            bonusActive={bonusActive}
            isInProgress={isInProgress}
            isModal={isModal}
            updateModalAlignment={updateModalAlignment}
            changeBonusHandler={changeBonusHandler}
          />
        ) : (
          <BonusSelectionListViewUI
            currency={currency}
            bonuses={bonuses}
            depositBonuses={depositBonuses}
            bonusActive={bonusActive}
            isInProgress={isInProgress}
            updateModalAlignment={updateModalAlignment}
            changeBonusHandler={changeBonusHandler}
          />
        )}
      </div>
    );
  }
}

export const BonusSelection = withImagesData(
  injectIntl(
    withUser(
      BonusSelectionUI, [
        USER_FIELDS.USER_DATA,
      ],
    ),
  )
);
